// ===============================================
// File: src/contexts/StaffContext.jsx
// Unified persistence via StorageContext (Folder OR Drive).
// - Loads on mount when workspace (folder/drive) available
// - Rehydrates after "mudo:vault-restored" and "mudo:files-restored"
// - Debounced saves; toasts on load/save errors
// - Back-compat: reads legacy "staff.json" if "mudoStaff.json" missing
// ===============================================

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";
import { useStorage } from "./StorageContext";
import { useNotifications } from "./NotificationsContext";

const FILE_PRIMARY = "mudoStaff.json";  // existing path
const FILE_LEGACY  = "staff.json";      // fallback read only

export const StaffContext = createContext({
  staffList: [],
  addStaff: () => {},
  updateStaff: () => {},
  deleteStaff: () => {},
  clearStaff: () => {},
});

function toast(message, type = "info") {
  try {
    window.dispatchEvent(new CustomEvent("mudo:toast", { detail: { type, message } }));
  } catch {}
}

function safeId() {
  try {
    return globalThis.crypto?.randomUUID?.()
      || `stf_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  } catch {
    return `stf_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  }
}

function normalizeStaff(s) {
  const id = String(s?.id || safeId());
  const now = Date.now();
  return {
    id,
    name: String(s?.name ?? "").trim(),
    role: String(s?.role ?? "").trim(),
    hourlyRate: Number(s?.hourlyRate ?? 0) || 0,
    hoursPerWeek: Number(s?.hoursPerWeek ?? 0) || 0,
    notes: String(s?.notes ?? ""),
    createdAt: Number(s?.createdAt) || now,
    updatedAt: now,
    email: s?.email || "",
    color: s?.color || "",
    staffId: s?.staffId || "",
    isActive: s?.isActive !== false,
  };
}

export function StaffProvider({ children }) {
  const {
    hasFolder,
    isDriveLinked,
    isDriveAuthed,
    loadJSON,
    saveJSON,
  } = useStorage();
  const { notifyAndInbox } = useNotifications() || {};

  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);

  // stable refs
  const loadRef = useRef(loadJSON);
  const saveRef = useRef(saveJSON);
  useEffect(() => { loadRef.current = loadJSON; }, [loadJSON]);
  useEffect(() => { saveRef.current = saveJSON; }, [saveJSON]);

  const [staffList, setStaffList] = useState([]);
  const loadedRef = useRef(false);
  const saveDebounce = useRef(0);

  const notifyStaff = useCallback(
    ({ id, message, level = "info" }) => {
      toast(message, level);
      notifyAndInbox?.({
        id,
        createdAt: Date.now(),
        level,
        title: "Staff",
        message,
        source: "system",
        world: "studio",
        entity: { type: "staff" },
        action: { kind: "NAVIGATE", to: "/dashboard/staff" },
      });
    },
    [notifyAndInbox]
  );

  // ----- hydrate on workspace availability
  useEffect(() => {
    let alive = true;
    loadedRef.current = false;

    (async () => {
      if (!hasWorkspace) {
        if (alive) setStaffList([]);
        loadedRef.current = true;
        return;
      }
      try {
        // try primary, then legacy
        let disk = await loadRef.current(FILE_PRIMARY, null);
        if (!Array.isArray(disk)) {
          const legacy = await loadRef.current(FILE_LEGACY, null);
          if (Array.isArray(legacy)) disk = legacy;
        }
        if (alive) setStaffList(Array.isArray(disk) ? disk : []);
      } catch (e) {
        notifyStaff({ id: "staff-load-failed", message: "Failed to load staff", level: "error" });
        if (alive) setStaffList([]);
      } finally {
        loadedRef.current = true;
      }
    })();

    return () => { alive = false; };
  }, [hasWorkspace, notifyStaff]);

  // ----- persist when list changes (debounced, only if workspace)
  useEffect(() => {
    if (!loadedRef.current || !hasWorkspace) return;
    clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(async () => {
      try { await saveRef.current(FILE_PRIMARY, staffList); }
      catch { notifyStaff({ id: "staff-save-failed", message: "Failed to save staff", level: "error" }); }
    }, 300);
    return () => clearTimeout(saveDebounce.current);
  }, [staffList, hasWorkspace, notifyStaff]);

  // ----- rehydrate after Receive (vault/files restored)
  useEffect(() => {
    const onRestored = async () => {
      if (!hasWorkspace) return;
      try {
        let disk = await loadRef.current(FILE_PRIMARY, null);
        if (!Array.isArray(disk)) {
          const legacy = await loadRef.current(FILE_LEGACY, null);
        if (Array.isArray(legacy)) disk = legacy;
      }
      setStaffList(Array.isArray(disk) ? disk : []);
    } catch {
      notifyStaff({
        id: "staff-rehydrate-failed",
        message: "Failed to rehydrate staff after restore",
        level: "error",
      });
    }
  };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      setStaffList([]);
      loadedRef.current = false;
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [hasWorkspace, notifyStaff]);

  /* ---------- CRUD ---------- */
  const addStaff = useCallback((s) => {
    const staff = normalizeStaff(s || {});
    setStaffList((prev) => [staff, ...(Array.isArray(prev) ? prev : [])]);
  }, []);

  // supports updateStaff(id, patch) OR updateStaff({ id, ...patch })
  const updateStaff = useCallback((idOrObj, maybePatch) => {
    let id = idOrObj;
    let patch = maybePatch;
    if (typeof idOrObj === "object" && idOrObj !== null && maybePatch === undefined) {
      id = idOrObj.id;
      patch = idOrObj;
    }
    if (!id) return;

    setStaffList((prev) =>
      (Array.isArray(prev) ? prev : []).map((s) =>
        String(s.id) === String(id)
          ? normalizeStaff({ ...s, ...patch, id: s.id, createdAt: s.createdAt })
          : s
      )
    );
  }, []);

  const deleteStaff = useCallback((id) => {
    setStaffList((prev) => (Array.isArray(prev) ? prev : []).filter((s) => String(s.id) !== String(id)));
  }, []);

  const clearStaff = useCallback(() => {
    setStaffList([]);
    clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(async () => {
      try { await saveRef.current(FILE_PRIMARY, []); }
      catch {
        notifyStaff({
          id: "staff-clear-failed",
          message: "Failed to clear staff on disk",
          level: "error",
        });
      }
    }, 50);
  }, [notifyStaff]);

  const value = useMemo(
    () => ({ staffList, addStaff, updateStaff, deleteStaff, clearStaff }),
    [staffList, addStaff, updateStaff, deleteStaff, clearStaff]
  );

  return <StaffContext.Provider value={value}>{children}</StaffContext.Provider>;
}

export const useStaff = () => useContext(StaffContext);
